import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion"; // Ensure you run: npm install framer-motion
import emailjs from "@emailjs/browser"; // Ensure you run: npm install @emailjs/browser

const HotelPartnership = () => {
  // --- 1. Top-Level State & Refs ---
  const [isFormOpen, setIsFormOpen] = useState(false);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const formRef = useRef();

  // --- 2. Theme Constants ---
  const tealMain = "#4da6a6"; 
  const charcoal = "#111827";
  const boneWhite = "#f9fafb";
  const whiteText = "#ffffff";
  const darkText = "#1f2937"; 
  const lightTealBg = "#f4f9f9"; 

  // --- 3. Animation Variants ---
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.3, delayChildren: 0.2 },
    },
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1, 
      transition: { duration: 0.6, ease: "easeOut" } 
    },
  };

  // --- 4. Step Data for Section 5 Journey ---
  const steps = [
    {
      id: 1,
      title: "Apply / Connect",
      desc: "Hotels submit a partnership request or connect with our specialized team to start.",
      icon: "📍",
    },
    {
      id: 2,
      title: "Strategic Discovery",
      desc: "We analyze your brand positioning, target audience, and collaboration needs.",
      icon: "🤝",
    },
    {
      id: 3,
      title: "Planning & Onboarding",
      desc: "We design the right influencer campaign or verification onboarding plan.",
      icon: "📋",
    },
    {
      id: 4,
      title: "Execution & Growth",
      desc: "Content creation, promotion, and long-term partnership support.",
      icon: "✨",
    },
  ];

  // --- 5. Logic: Email & Scrolling ---
  const sendEmail = (e) => {
    e.preventDefault();
    const SERVICE_ID = "service_398n29y"; 
    const TEMPLATE_ID = "template_rt4m2nu";
    const PUBLIC_KEY = "fFdw0sKjG6hYkRS5b";
    

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, PUBLIC_KEY)
      .then(() => {
          alert("Verification application submitted! We will contact you soon.");
          setIsFormOpen(false);
      }, () => {
          alert("Submission failed. Please check your connection or try again.");
      });
  };

  useEffect(() => {
    if (hash) {
      const timer = setTimeout(() => {
        const element = document.getElementById(hash.replace("#", ""));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100); 
      return () => clearTimeout(timer);
    }
  }, [hash]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  // --- 6. Normalized Editorial Styles ---
  const labelStyle = { display: "block", fontSize: "12px", fontWeight: "700", color: "#6b7280", textTransform: "uppercase", marginBottom: "8px", letterSpacing: "1px", fontFamily: "Inter, sans-serif" };
  const fieldStyle = { width: "100%", padding: "14px 16px", background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "2px", fontSize: "16px", color: charcoal, outline: "none", boxSizing: "border-box" };
  const sectionPadding = "100px 24px";
  const containerWidth = { maxWidth: "1100px", margin: "0 auto" };

  // --- 7. Reusable Button ---
  const ActionButton = ({ text, inverted = false, onClick, extraStyle = {} }) => {
    const [isHovered, setIsHovered] = useState(false);
    const baseStyle = { padding: "14px 40px", borderRadius: "50px", fontSize: "18px", fontWeight: "700", cursor: "pointer", transition: "all 0.3s ease", fontFamily: "'Cormorant Garamond', serif", textDecoration: "none", display: "inline-block", textAlign: "center", border: inverted ? "2px solid #fff" : `2px solid ${tealMain}`, backgroundColor: "transparent", color: inverted ? "#fff" : tealMain, letterSpacing: "0.5px", ...extraStyle };
    const hoverStyle = { backgroundColor: extraStyle.backgroundColor ? "#ffffff" : (inverted ? "#fff" : tealMain), color: extraStyle.backgroundColor ? extraStyle.backgroundColor : (inverted ? "#111827" : "#fff"), transform: "translateY(-3px)", boxShadow: "0 10px 20px rgba(0,0,0,0.15)" };
    return (
      <button style={isHovered ? { ...baseStyle, ...hoverStyle } : baseStyle} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={onClick}>
        {text}
      </button>
    );
  };

  return (
    <div style={{ backgroundColor: "#0f172a", color: whiteText, overflowX: "hidden" }}>
      
      {/* SECTION 1: HERO - RESTORED CONTENT & FULL VIEWPORT */}
      <section style={{
        height: "100vh", // Full screen resolution
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: "url('services/new_page_hero.jpeg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed"
      }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", color: whiteText, maxWidth: "900px", padding: "0 24px" }}>
          <h1 style={{ 
            fontFamily: "'Cormorant Garamond', serif", 
            fontSize: "56px", 
            marginBottom: "20px", 
            lineHeight: "1.2", 
            fontWeight: "bold" 
          }}>
            Helping Hotels Grow Through Influence & Trust.
          </h1>
          <p style={{ 
            fontSize: "24px", 
            marginBottom: "40px", 
            opacity: 1, 
            fontFamily: "'Cormorant Garamond', serif",
            maxWidth: "750px",
            margin: "0 auto 40px"
          }}>
            TravXplo partners with hotels and resorts to build visibility, credibility, and meaningful traveler engagement.
          </p>
          <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap" }}>
            <ActionButton 
              text="Influencer Collaborations" 
              inverted={true} 
              onClick={() => scrollToSection('influencer-program')} 
            />
            <ActionButton 
              text="TravXplo Verified Program" 
              inverted={true} 
              onClick={() => scrollToSection('verified-program')} 
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: WHO WE WORK WITH - FULL TRANSPARENCY ON LEFT SIDE */}
      <section style={{ 
        padding: sectionPadding, 
        backgroundColor: lightTealBg, 
        color: darkText,
        backgroundImage: `linear-gradient(rgba(244, 249, 249, 0.8), rgba(244, 249, 249, 0.8)), url('blog-background.jpg')`, 
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        position: "relative"
      }}>
        <div style={containerWidth}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "60px" }}>
            
            {/* Transparent Logo Area */}
            <div style={{ flex: "1 1 400px" }}>
              <div style={{ 
                width: "100%", 
                height: "450px", 
                borderRadius: "20px", 
                overflow: "hidden", 
                position: "relative",
                backgroundColor: "transparent", // Set to transparent
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                padding: "40px"
              }}>
                <img 
                  src="logo/travxplo_transparent.png" 
                  alt="TravXplo Logo" 
                  style={{ 
                    width: "100%", 
                    height: "auto", 
                    objectFit: "contain", 
                    opacity: "1" // Reset opacity to full visibility against background
                  }} 
                />
              </div>
            </div>

            {/* Text Content Area */}
            <div style={{ flex: "1 1 500px" }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "48px", marginBottom: "24px", fontWeight: "bold" }}>
                Who We Work With
              </h2>
              <p style={{ fontSize: "18px", lineHeight: "1.7", marginBottom: "30px", opacity: 0.9, fontWeight: "500" }}>
                TravXplo works with boutique hotels, luxury resorts, and experience-driven properties seeking to build traveler trust through modern strategies.
              </p>
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                {[
                  "Boutique Hotels", 
                  "Lifestyle Resorts", 
                  "New Properties", 
                  "Experience Stays"
                ].map((text, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "18px", fontWeight: "700" }}>
                    <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: tealMain }} /> 
                    {text}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 3: INFLUENCER PROGRAM - OPTIMIZED RESOLUTION & CLARITY */}
      <section id="influencer-program" style={{ 
        padding: "100px 24px", 
        color: darkText,
        backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url('services/bg.jpeg')", 
        backgroundSize: "cover", 
        backgroundPosition: "center", 
        backgroundAttachment: "fixed", 
        position: "relative" 
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          
          {/* Header Section */}
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <h2 style={{ 
              fontFamily: "'Cormorant Garamond', serif", 
              fontSize: "52px", 
              color: charcoal, 
              fontWeight: "bold", 
              marginBottom: "15px", 
              lineHeight: "1.2" 
            }}>
              Influencer Collaboration Program
            </h2>
            <p style={{ 
              fontSize: "22px", 
              color: "#1a1a1a", 
              maxWidth: "800px", 
              fontWeight: '800', 
              margin: "0 auto", 
              lineHeight: "1.6", 
              fontFamily: "'Cormorant Garamond', serif" 
            }}>
              We connect distinguished hotels and resorts with curated travel creators to build authentic content and meaningful visibility.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "30px", justifyContent: "center" }}>
            
            {/* Card 1 — What Hotels Receive */}
            <div 
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-15px) scale(1.02)";
                e.currentTarget.style.boxShadow = "0 30px 60px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.05)";
              }}
              style={{ 
                flex: "1 1 450px", 
                height: "550px", 
                borderRadius: "28px", 
                overflow: "hidden", 
                position: "relative", 
                backgroundImage: "url('services/gets.png')", 
                backgroundSize: "cover", 
                backgroundPosition: "center",
                transition: "all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1)", 
                boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                display: "flex", 
                flexDirection: "column", 
                justifyContent: "flex-end",
                padding: "40px",
                cursor: "pointer"
              }}
            >
              {/* Three-step gradient for better image clarity at the top */}
              <div style={{ 
                position: "absolute", 
                inset: 0, 
                background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)", 
                zIndex: 1 
              }} />
              
              <div style={{ position: "relative", zIndex: 2 }}>
                <h3 style={{ fontSize: "32px", color: whiteText, fontFamily: "'Cormorant Garamond', serif", marginBottom: "25px", fontWeight: "bold", borderLeft: `3px solid ${tealMain}`, paddingLeft: "15px" }}>
                  What Hotels Receive
                </h3>
                <div style={{ display: "grid", gap: "20px" }}>
                  <div>
                    <h4 style={{ fontSize: "18px", color: whiteText, fontWeight: "700", marginBottom: "4px" }}>✦ Curated Creators</h4>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", lineHeight: "1.4" }}>Influencers selected to match your brand's standards.</p>
                  </div>
                  <div>
                    <h4 style={{ fontSize: "18px", color: whiteText, fontWeight: "700", marginBottom: "4px" }}>✦ Bespoke Assets</h4>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", lineHeight: "1.4" }}>Professional photography and video for your archives.</p>
                  </div>
                  <div>
                    <h4 style={{ fontSize: "18px", color: whiteText, fontWeight: "700", marginBottom: "4px" }}>✦ Enhanced Visibility</h4>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", lineHeight: "1.4" }}>Meaningful exposure across targeted traveler communities.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 — Our Management */}
            <div 
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-15px) scale(1.02)";
                e.currentTarget.style.boxShadow = "0 30px 60px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 20px rgba(0,0,0,0.05)";
              }}
              style={{ 
                flex: "1 1 450px", 
                height: "550px", 
                borderRadius: "28px", 
                overflow: "hidden", 
                position: "relative", 
                backgroundImage: "url('services/manage.png')", 
                backgroundSize: "cover", 
                backgroundPosition: "center",
                transition: "all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1)", 
                boxShadow: "0 10px 20px rgba(0,0,0,0.05)",
                display: "flex", 
                flexDirection: "column", 
                justifyContent: "flex-end",
                padding: "40px",
                cursor: "pointer"
              }}
            >
              <div style={{ 
                position: "absolute", 
                inset: 0, 
                background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)", 
                zIndex: 1 
              }} />
              
              <div style={{ position: "relative", zIndex: 2 }}>
                <h3 style={{ fontSize: "32px", color: whiteText, fontFamily: "'Cormorant Garamond', serif", marginBottom: "25px", fontWeight: "bold", borderLeft: "3px solid #fff", paddingLeft: "15px" }}>
                  Our Management
                </h3>
                <div style={{ display: "grid", gap: "20px" }}>
                  <div>
                    <h4 style={{ fontSize: "18px", color: whiteText, fontWeight: "700", marginBottom: "4px" }}>✦ End-to-End Vetting</h4>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", lineHeight: "1.4" }}>Rigorous standards for every creator we collaborate with.</p>
                  </div>
                  <div>
                    <h4 style={{ fontSize: "18px", color: whiteText, fontWeight: "700", marginBottom: "4px" }}>✦ Logistics Coordination</h4>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", lineHeight: "1.4" }}>Seamless management of communication and timelines.</p>
                  </div>
                  <div>
                    <h4 style={{ fontSize: "18px", color: whiteText, fontWeight: "700", marginBottom: "4px" }}>✦ Content Oversight</h4>
                    <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.85)", lineHeight: "1.4" }}>Ensuring every deliverable aligns with your standards.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Footer */}
          <div style={{ textAlign: "center", marginTop: "80px" }}>
            <ActionButton 
              text="Partner with TravXplo" 
              onClick={() => navigate('/HotelCollabForm')} 
              extraStyle={{ 
                padding: "16px 60px", 
                fontSize: "22px", 
                backgroundColor: "#1e293b", 
                color: "#fff", 
                borderColor: "#1e293b" 
              }} 
            />
          </div>
        </div>
      </section>

      {/* SECTION 4: VERIFIED PROGRAM - RESTORED CONTENT */}
      <section id="verified-program" style={{ 
        padding: "160px 24px", 
        backgroundColor: boneWhite, 
        color: charcoal, 
        borderTop: "1px solid #eaeaea",
        position: "relative"
      }}>
        <div style={containerWidth}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "80px", alignItems: "center" }}>
            
            {/* Left Column: Accreditation Details */}
            <div style={{ flex: "1 1 600px" }}>
              {/* <span style={{ 
                fontSize: "14px", 
                fontWeight: "700", 
                letterSpacing: "3px", 
                color: tealMain, 
                textTransform: "uppercase" 
              }}>
                Accreditation
              </span> */}
              
              <h2 style={{ 
                fontFamily: "'Cormorant Garamond', serif", 
                fontSize: "72px", 
                margin: "20px 0 30px", 
                fontWeight: "bold", 
                lineHeight: "1.1",
                color: charcoal
              }}>
                TravXplo Verified Program
              </h2>
              
              <p style={{ 
                fontSize: "24px", 
                color: "#4b5563", 
                lineHeight: "1.6", 
                marginBottom: "45px",
                maxWidth: "550px"
              }}>
                A verification and visibility program designed to help trusted hotels stand out and build traveler confidence.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px" }}>
                <div>
                  <h4 style={{ fontSize: "14px", fontWeight: "900", textTransform: "uppercase", color: "#000000", marginBottom: "15px" }}>Offering</h4>
                  <ul style={{ listStyle: "none", padding: 0, fontSize: "19px", lineHeight: "2.5", color: charcoal }}>
                    <li>✦ Official Badge</li>
                    <li>✦ Search Priority</li>
                    <li>✦ Featured Placement</li>
                    <li>✦ Dedicated Support</li>
                  </ul>
                </div>
                <div>
                  <h4 style={{ fontSize: "14px", fontWeight: "900", textTransform: "uppercase", color: "#000000", marginBottom: "15px" }}>Standard</h4>
                  <ul style={{ listStyle: "none", padding: 0, fontSize: "19px", lineHeight: "2.5", color: charcoal }}>
                    <li>✦ Authenticity</li>
                    <li>✦ Reliability</li>
                    <li>✦ Excellence</li>
                    <li>✦ Transparency</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column: CTA Card */}
            <div style={{ 
              flex: "1 1 420px", 
              padding: "70px 60px", 
              backgroundColor: charcoal, 
              borderRadius: "4px", 
              textAlign: "center", 
              color: "#fff", 
              boxShadow: "0 40px 80px rgba(0,0,0,0.15)" 
            }}>
              <h3 style={{ 
                fontFamily: "'Cormorant Garamond', serif", 
                fontSize: "42px", 
                marginBottom: "20px",
                lineHeight: "1.2"
              }}>
                Verification <br /> Request
              </h3>
              <p style={{ fontSize: "18px", opacity: 1, marginBottom: "40px" }}>
                Join our elite circle of recognized properties.
              </p>
              
              <button 
                onClick={() => setIsFormOpen(true)} 
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = tealMain;
                  e.target.style.transform = "scale(1.05)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#fff";
                  e.target.style.transform = "scale(1)";
                }}
                style={{ 
                  padding: "20px 50px", 
                  background: "#fff", 
                  color: charcoal, 
                  border: "none", 
                  borderRadius: "2px", 
                  fontWeight: "bold", 
                  cursor: "pointer", 
                  textTransform: "uppercase", 
                  fontSize: "16px", 
                  letterSpacing: "2px", 
                  transition: "all 0.3s ease" 
                }}
              >
                Start Application
              </button>
            </div>

          </div>
        </div>

        {/* INTEGRATED EMAIL MODAL LOGIC - UPDATED WITH BG IMAGE */}
        {isFormOpen && (
          <div style={{ 
            position: "fixed", 
            inset: 0, 
            backgroundColor: "rgba(0, 0, 0, 0.7)", // Darker backdrop to make the form pop
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            zIndex: 10000, 
            padding: "20px" 
          }}>
            <div style={{ 
              width: "100%", 
              maxWidth: "800px", 
              position: "relative", 
              maxHeight: "95vh", 
              overflowY: "auto", 
              padding: "50px",
              borderRadius: "12px",
              backgroundColor: "#fff", // Fallback color
              /* Added Background Image with a soft white overlay for legibility */
              backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)), url('blog-background.jpg')`, 
              backgroundSize: "cover",
              backgroundPosition: "center",
              boxShadow: "0 50px 100px rgba(0,0,0,0.3)"
            }}>
              {/* Close Button */}
              <button 
                onClick={() => setIsFormOpen(false)} 
                style={{ 
                  position: "absolute", 
                  top: "20px", 
                  right: "25px", 
                  background: "none", 
                  border: "none", 
                  fontSize: "35px", 
                  cursor: "pointer", 
                  color: charcoal,
                  zIndex: 10
                }}
              >
                ×
              </button>

              <h3 style={{ 
                fontFamily: "'Cormorant Garamond', serif", 
                fontSize: "42px", 
                textAlign: "center", 
                marginBottom: "10px", 
                color: charcoal,
                fontWeight: "bold" 
              }}>
                Verification Application
              </h3>
              <p style={{ textAlign: "center", marginBottom: "40px", color: "#666", fontSize: "16px" }}>
                Please provide your property details to begin the accreditation process.
              </p>
              
              <form ref={formRef} onSubmit={sendEmail}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Property Name</label>
                    <input type="text" name="property_name" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required />
                  </div>
                  <div>
                    <label style={labelStyle}>Location</label>
                    <input type="text" name="location" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required />
                  </div>
                  <div>
                    <label style={labelStyle}>Contact Person</label>
                    <input type="text" name="contact_name" placeholder="Full Name" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required />
                  </div>
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input type="email" name="user_email" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required />
                  </div>
                  <div>
                    <label style={labelStyle}>WhatsApp Number</label>
                    <input type="text" name="whatsapp" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required />
                  </div>
                  <div>
                    <label style={labelStyle}>Property Type</label>
                    <select name="property_type" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required>
                      <option value="">Select Option</option>
                      <option>Boutique Hotel</option>
                      <option>Luxury Resort</option>
                      <option>Villa</option>
                      <option>Homestay</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Website / Social Link</label>
                    <input type="text" name="social_link" placeholder="Link" style={{...fieldStyle, backgroundColor: "rgba(255,255,255,0.8)"}} required />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Why join TravXplo?</label>
                    <textarea name="message" placeholder="Align with Verified standards..." style={{ ...fieldStyle, height: "120px", resize: "none", backgroundColor: "rgba(255,255,255,0.8)" }} required />
                  </div>
                </div>
                <button 
                  type="submit" 
                  style={{ 
                    width: "100%", 
                    padding: "18px", 
                    background: charcoal, 
                    color: "#fff", 
                    border: "none", 
                    borderRadius: "4px", 
                    fontWeight: "bold", 
                    marginTop: "30px", 
                    textTransform: "uppercase", 
                    fontSize: "15px", 
                    letterSpacing: "2px", 
                    cursor: "pointer",
                    transition: "all 0.3s ease" 
                  }}
                >
                  Submit Verification Application
                </button>
              </form>
            </div>
          </div>
        )}
      </section>

      {/* SECTION 5: HOW IT WORKS - JOURNEY MAP */}
      <section id="how-it-works" style={{ padding: "120px 24px", backgroundColor: "#ffffff", position: "relative", overflow: "hidden" }}>
        <div style={containerWidth}>
          <div style={{ textAlign: "center", marginBottom: "80px" }}>
            <motion.h2 initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "52px", fontWeight: "bold", color: charcoal }}>How It Works</motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} style={{ fontSize: "18px", color: "#6b7280" }}>A simple guided journey to start growing with TravXplo.</motion.p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "40px", position: "relative" }}>
            {/* Desktop Path Line */}
            <div style={{ position: "absolute", top: "50px", left: "10%", right: "10%", height: "2px", background: "rgba(77, 166, 166, 0.2)", zIndex: 0 }}>
              <motion.div initial={{ width: 0 }} whileInView={{ width: "100%" }} transition={{ duration: 1.5, ease: "easeInOut" }} style={{ height: "100%", background: tealMain, position: "relative" }}>
                 <div style={{ position: "absolute", right: 0, top: "-4px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: tealMain, boxShadow: `0 0 10px ${tealMain}` }} />
              </motion.div>
            </div>

            {steps.map((step) => (
              <motion.div key={step.id} variants={cardVariants} whileHover={{ y: -10 }} style={{ flex: "1 1 250px", maxWidth: "280px", zIndex: 1, textAlign: "center" }}>
                <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: "#fff", border: `2px solid ${tealMain}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: "32px", boxShadow: "0 10px 20px rgba(0,0,0,0.05)" }}>
                  {step.icon}
                </div>
                <h4 style={{ fontSize: "20px", fontWeight: "bold", color: charcoal, marginBottom: "12px" }}>{step.id}. {step.title}</h4>
                <p style={{ fontSize: "15px", color: "#4b5563", lineHeight: "1.6" }}>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: COLLABORATIONS & CAMPAIGN HIGHLIGHTS - HIGH VISIBILITY BG */}
      <section id="campaign-highlights" style={{ 
        padding: "100px 24px", 
        position: "relative",
        overflow: "hidden",
        /* Decreased overlay opacity from 0.92 to 0.7 for better image visibility */
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url('blog-background.jpg')`, 
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed", 
        borderTop: "1px solid #f0f0f0"
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", position: "relative", zIndex: 2 }}>
          
          {/* Header Section */}
          <div style={{ textAlign: "center", marginBottom: "80px" }}>
            <motion.h2 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ 
                fontFamily: "'Cormorant Garamond', serif", 
                fontSize: "48px", 
                fontWeight: "bold", 
                color: "#111827",
                marginBottom: "10px",
                /* Added text shadow for readability against clearer BG */
                textShadow: "0 2px 4px rgba(255,255,255,0.5)"
              }}
            >
              Collaborations & Campaign Highlights
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{ 
                fontSize: "18px", 
                color: "#1f2937", 
                fontFamily: "Inter, sans-serif",
                fontWeight: "500" 
              }}
            >
              See how brands grow with TravXplo partnerships.
            </motion.p>
          </div>

          {/* Dynamic Grid System */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "30px",
            position: "relative"
          }}>
            
            {/* 3D Breathing Placeholder Cards */}
            {[1, 2, 3].map((item) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, rotateY: 15, y: 40 }}
                whileInView={{ opacity: 1, rotateY: 0, y: 0 }}
                whileHover={{ 
                  scale: 1.04, 
                  rotateX: 2, 
                  rotateY: -2,
                  transition: { duration: 0.4 } 
                }}
                transition={{ 
                  type: "spring", 
                  stiffness: 40, 
                  damping: 15, 
                  delay: item * 0.2 
                }}
                viewport={{ once: true }}
                style={{
                  height: "450px",
                  borderRadius: "28px",
                  /* Slightly higher opacity for cards to maintain pop against clearer BG */
                  backgroundColor: "rgba(255, 255, 255, 0.95)", 
                  backdropFilter: "blur(8px)",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 25px 50px rgba(0,0,0,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  perspective: "1000px" 
                }}
              >
                {/* Breathing Radial Aura Shimmer */}
                <motion.div
                  animate={{ 
                    opacity: [0.3, 0.6, 0.3],
                    scale: [1, 1.15, 1] 
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  style={{
                    position: "absolute",
                    width: "140%",
                    height: "140%",
                    background: "radial-gradient(circle, rgba(77, 166, 166, 0.2) 0%, transparent 65%)",
                    zIndex: 1
                  }}
                />
                
                <div style={{ textAlign: "center", zIndex: 2, opacity: 0.6 }}>
                  <motion.div 
                    animate={{ y: [0, -12, 0] }} 
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    style={{ fontSize: "44px", marginBottom: "15px" }}
                  >
                    🎬
                  </motion.div>
                  <h4 style={{ fontSize: "13px", fontWeight: "800", color: "#4da6a6", textTransform: "uppercase", letterSpacing: "4px" }}>
                    Coming Soon
                  </h4>
                </div>
              </motion.div>
            ))}

            {/* Floating Centered Hub Message */}
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
              textAlign: "center",
              width: "100%",
              pointerEvents: "none"
            }}>
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  display: "inline-block",
                  padding: "26px 52px",
                  background: "rgba(255, 255, 255, 1)",
                  borderRadius: "100px",
                  boxShadow: "0 35px 70px rgba(0,0,0,0.15)",
                  border: "1px solid rgba(77, 166, 166, 0.3)",
                  backdropFilter: "blur(15px)"
                }}
              >
                <h3 style={{ 
                  fontFamily: "'Cormorant Garamond', serif", 
                  fontSize: "28px", 
                  fontWeight: "bold", 
                  color: "#111827",
                  margin: 0,
                  letterSpacing: "0.5px"
                }}>
                  Upcoming collaborations launching soon.
                </h3>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: WHY PARTNER WITH TRAVXPLO - ENHANCED IMAGE VISIBILITY */}
      <section id="why-partner" style={{ 
        padding: "120px 24px", 
        position: "relative",
        overflow: "hidden",
        /* Overlay opacity reduced to 0.6 for maximum image clarity */
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)), url('blog-background.jpg')`, 
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed", 
        color: charcoal 
      }}>
        <div style={{ ...containerWidth, position: "relative", zIndex: 2 }}>
          
          {/* Section Header with Text Shadow for Readability */}
          <div style={{ textAlign: "center", marginBottom: "80px" }}>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{ 
                fontFamily: "'Cormorant Garamond', serif", 
                fontSize: "52px", 
                fontWeight: "bold", 
                marginBottom: "15px",
                textShadow: "0 2px 10px rgba(255,255,255,0.8)" // Protects text from BG noise
              }}
            >
              Why Partner with TravXplo
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              style={{ 
                fontSize: "19px", 
                color: "#111827", 
                fontFamily: "Inter, sans-serif",
                fontWeight: "600",
                textShadow: "0 1px 5px rgba(255,255,255,0.8)" 
              }}
            >
              A partnership built for sustainable hospitality growth.
            </motion.p>
          </div>

          {/* Feature Grid */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
              gap: "30px",
              marginBottom: "80px"
            }}
          >
            {[
              {
                icon: "🌍",
                title: "Travel-Focused Platform",
                desc: "We connect travelers actively looking for stays and experiences."
              },
              {
                icon: "🛎️",
                title: "Hospitality-First Approach",
                desc: "We understand hotels — not just marketing metrics."
              },
              {
                icon: "📸",
                title: "Influencer-Driven Growth",
                desc: "Reach modern travelers through curated travel creators."
              },
              {
                icon: "🤝",
                title: "Long-Term Partnership Mindset",
                desc: "We grow with our partners, not just run one-time campaigns."
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                variants={cardVariants}
                whileHover={{ 
                  y: -10, 
                  boxShadow: "0 30px 60px rgba(0,0,0,0.12)",
                  borderColor: tealMain
                }}
                style={{
                  padding: "45px 30px",
                  /* Increased card opacity to 0.98 to keep them solid against clear BG */
                  backgroundColor: "rgba(255, 255, 255, 0.98)", 
                  backdropFilter: "blur(12px)",
                  borderRadius: "20px",
                  border: "1px solid rgba(0,0,0,0.04)",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  cursor: "default"
                }}
              >
                <motion.div 
                  whileHover={{ scale: 1.1 }}
                  style={{ fontSize: "44px", marginBottom: "20px" }}
                >
                  {feature.icon}
                </motion.div>
                <h4 style={{ 
                  fontSize: "21px", 
                  fontWeight: "800", 
                  marginBottom: "15px", 
                  fontFamily: "Inter, sans-serif",
                  color: charcoal
                }}>
                  {feature.title}
                </h4>
                <p style={{ 
                  fontSize: "15px", 
                  color: "#374151", 
                  lineHeight: "1.6",
                  fontWeight: "500"
                }}>
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Reassurance Line */}
          <div style={{ textAlign: "center" }}>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              style={{ 
                fontSize: "18px", 
                color: charcoal, 
                fontStyle: "italic",
                fontWeight: "700",
                borderTop: `2px solid ${tealMain}`,
                display: "inline-block",
                paddingTop: "25px",
                textShadow: "0 2px 10px rgba(255,255,255,1)"
              }}
            >
              “Built for hotels that value quality guests, not just quantity.”
            </motion.p>
          </div>
        </div>
      </section>

      {/* SECTION 8: FINAL CALL TO ACTION - RESTORED BUTTON UI & CLASSIC THEME */}
      <section style={{ 
        padding: "160px 24px", 
        position: "relative", 
        overflow: "hidden", 
        textAlign: "center",
        /* High-visibility background image with optimized overlay */
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)), url('blog-background.jpg')`, 
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed" 
      }}>
        {/* Subtle Shifting Gradient Overlay for Depth */}
        <motion.div
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          style={{ 
            position: "absolute", 
            inset: 0, 
            opacity: 0.4,
            background: "linear-gradient(135deg, #111827 0%, #1e293b 50%, #0f172a 100%)",
            backgroundSize: "200% 200%",
            zIndex: 1
          }}
        />

        {/* Floating Particles for Premium Feel */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            animate={{ 
              y: [0, -40, 0], 
              opacity: [0.1, 0.3, 0.1],
              scale: [1, 1.2, 1] 
            }}
            transition={{ 
              duration: 5 + i, 
              repeat: Infinity, 
              delay: i * 0.5 
            }}
            style={{
              position: "absolute",
              width: "4px",
              height: "4px",
              backgroundColor: "#4da6a6", // Brand Teal
              borderRadius: "50%",
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 20}%`,
              filter: "blur(2px)",
              zIndex: 2
            }}
          />
        ))}

        <div style={{ maxWidth: "1100px", margin: "0 auto", position: "relative", zIndex: 3 }}>
          {/* Subtle Logo Watermark */}
          <div style={{ marginBottom: "40px", opacity: 0.15 }}>
            <img 
               src="logo/travxplo_transparent.png" 
               alt="Watermark" 
               style={{ width: "180px", filter: "brightness(0) invert(1)" }} 
            />
          </div>

          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{ 
              fontFamily: "'Cormorant Garamond', serif", 
              fontSize: "56px", 
              fontWeight: "bold", 
              color: "#ffffff", 
              marginBottom: "24px",
              lineHeight: "1.2"
            }}
          >
            Let’s Build Something Meaningful Together.
          </motion.h2>

          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            style={{ 
              fontSize: "20px", 
              color: "rgba(255, 255, 255, 0.95)", 
              maxWidth: "700px", 
              margin: "0 auto 50px", 
              lineHeight: "1.8",
              fontFamily: "Inter, sans-serif"
            }}
          >
            Whether you’re looking to collaborate with influencers or position your property as a trusted stay, TravXplo would love to work with you.
          </motion.p>

          {/* Single Action Button - RESTORED PILL UI */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            style={{ 
              display: "flex", 
              justifyContent: "center", 
              marginBottom: "50px" 
            }}
          >
            <ActionButton 
              text="Partner with TravXplo" 
              inverted={true} 
              onClick={() => setIsFormOpen(true)}
              extraStyle={{ 
                borderRadius: "50px", // Restored Pill/Rounded UI
                padding: "18px 60px",
                textTransform: "none", // Matches original "Talk to Our Team" casing
                fontSize: "18px",
                fontWeight: "600",
                borderColor: "#ffffff", 
                color: "#ffffff",
                transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)"
              }}
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 1 }}
            style={{ 
              fontSize: "14px", 
              color: "rgba(255, 255, 255, 0.6)", 
              letterSpacing: "1px",
              textTransform: "uppercase",
              fontWeight: "600"
            }}
          >
            No commitments. Just a conversation to explore possibilities.
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default HotelPartnership;